import type { JourneyStep } from "../../types/journey";

/**
 * M011/S04 T01: pure helper that resolves the date window a journey step (and
 * the tasks under it) should occupy in the Track page calendar + Coming Up
 * lists.
 *
 * Two sources, in precedence order:
 *
 * 1. `estimated` — step.estimatedStartDate (absolute ISO date) plus
 *    step.highlight.estimatedLengthDays. The window starts at
 *    estimatedStartDate and ends estimatedLengthDays-1 later (inclusive day
 *    range, so a length of 1 collapses to a single day). `journeyStartedAt`
 *    is accepted by the signature for symmetry with the upstream view model
 *    but is not used in the math — estimatedStartDate is absolute in the
 *    CDN journey JSON, not relative to the user's journey-start timestamp.
 * 2. `currentWeek` — fallback when either input is missing. We snap to the
 *    week containing `now`: Sunday 00:00 UTC → following Saturday 23:59:59 UTC.
 *    Pure UTC math; no time-zone library, no DST trickery.
 *
 * Pure: no React, no I/O, no Date.now reads. `now` must be passed in.
 */

export type TaskDateWindowSource = "estimated" | "currentWeek";

export interface TaskDateWindow {
	startIso: string;
	endIso: string;
	source: TaskDateWindowSource;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseIsoDate(value: string | undefined): Date | null {
	if (typeof value !== "string" || value.length === 0) return null;
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return null;
	return parsed;
}

function startOfUtcDay(d: Date): Date {
	return new Date(
		Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0),
	);
}

function endOfUtcDay(d: Date): Date {
	return new Date(
		Date.UTC(
			d.getUTCFullYear(),
			d.getUTCMonth(),
			d.getUTCDate(),
			23,
			59,
			59,
			999,
		),
	);
}

function currentWeekWindow(now: Date): TaskDateWindow {
	const dayOfWeek = now.getUTCDay(); // 0 = Sunday
	const sunday = new Date(now.getTime() - dayOfWeek * MS_PER_DAY);
	const saturday = new Date(sunday.getTime() + 6 * MS_PER_DAY);
	return {
		startIso: startOfUtcDay(sunday).toISOString(),
		endIso: endOfUtcDay(saturday).toISOString(),
		source: "currentWeek",
	};
}

export function deriveTaskDateWindow(
	step: Pick<JourneyStep, "estimatedStartDate" | "highlight">,
	_journeyStartedAt: string | null | undefined,
	now: Date,
): TaskDateWindow {
	const estimatedStart = parseIsoDate(step.estimatedStartDate);
	const lengthDays = step.highlight?.estimatedLengthDays;

	if (estimatedStart && typeof lengthDays === "number" && lengthDays > 0) {
		const start = startOfUtcDay(estimatedStart);
		const end = endOfUtcDay(
			new Date(start.getTime() + (lengthDays - 1) * MS_PER_DAY),
		);
		return {
			startIso: start.toISOString(),
			endIso: end.toISOString(),
			source: "estimated",
		};
	}

	return currentWeekWindow(now);
}
